import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Badge,
  Button,
  Input,
  PageContainer,
  Skeleton,
  Toggle,
  useToast,
} from '@bbpos/ui';
import { siteContentService } from '@/services/site-content.service';
import { SitePageBlockEditor } from '@/components/features/site/SitePageBlockEditor';
import {
  SITE_PAGE_HINTS,
  SITE_PAGE_KEYS,
  SITE_PAGE_LABELS,
  cleanBlocks,
  type SitePageBlock,
  type SitePageContent,
  type SitePageKey,
} from '@/types/site-content';

/**
 * The pages a shopkeeper writes for their own website.
 *
 * Seven of them, and the list cannot grow: it is frozen in a database CHECK on
 * an avnadmin-owned table that cannot be altered. So this renders the constant
 * rather than inviting an eighth that would fail with a constraint error.
 *
 * `/returns` is called out because publishing it does something beyond
 * rendering a page: it is what puts the shop's returns window into the
 * structured data on every product. The window itself is already enforced by
 * the billing code; publishing is the act that turns an internal rule into a
 * public promise.
 */

/**
 * Matches `ck_tspc_body_length`.
 *
 * The per-field length ceilings (80 for the title, 70 and 320 for the search
 * fields) live in the API DTO and the column types, and are stated in the
 * helper text rather than enforced here — `Input` takes no maxLength, and a
 * second copy of a number is one more thing that can disagree with the first.
 */
const MAX_BLOCKS = 40;

interface Draft {
  title: string;
  body: SitePageBlock[];
  meta_title: string;
  meta_description: string;
  noindex: boolean;
}

const draftFrom = (page: SitePageContent | undefined, key: SitePageKey): Draft => ({
  title: page?.title ?? SITE_PAGE_LABELS[key],
  body: page?.body ?? [],
  meta_title: page?.meta_title ?? '',
  meta_description: page?.meta_description ?? '',
  noindex: page?.noindex ?? false,
});

export function SiteContentPage() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [active, setActive] = useState<SitePageKey>('delivery');

  const { data: pages, isLoading, error } = useQuery({
    queryKey: ['site-content'],
    queryFn: () => siteContentService.list(),
  });

  const current = useMemo(
    () => pages?.find((p) => p.page_key === active),
    [pages, active],
  );

  const [draft, setDraft] = useState<Draft>(() => draftFrom(undefined, 'delivery'));

  // Reset whenever the selected page changes or the server answers. Editing one
  // page and switching to another must not carry the first one's blocks across.
  useEffect(() => {
    setDraft(draftFrom(current, active));
  }, [current, active]);

  const save = useMutation({
    mutationFn: ({ status }: { status: 'DRAFT' | 'PUBLISHED' }) =>
      siteContentService.save(active, {
        title: draft.title.trim(),
        // Blank blocks are a normal state halfway through writing, and the API
        // rejects them — so they are dropped here rather than turned into a 400
        // the shopkeeper has to decode.
        body: cleanBlocks(draft.body),
        status,
        meta_title: draft.meta_title.trim() || undefined,
        meta_description: draft.meta_description.trim() || undefined,
        noindex: draft.noindex,
      }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['site-content'] });
      toast({
        title: variables.status === 'PUBLISHED' ? 'Page published' : 'Draft saved',
        variant: 'success',
      });
    },
    onError: (err: Error) =>
      toast({ title: err.message || 'Could not save the page', variant: 'error' }),
  });

  const remove = useMutation({
    mutationFn: () => siteContentService.remove(active),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['site-content'] });
      toast({ title: 'Page deleted', variant: 'success' });
    },
    onError: (err: Error) => toast({ title: err.message || 'Could not delete', variant: 'error' }),
  });

  if (isLoading) return <Skeleton className="h-64 w-full" />;
  if (error) {
    return (
      <PageContainer title="Website pages">
        <Alert
          variant="danger"
          description="Could not load your pages. If this shop has no website yet, a platform admin sets one up first."
        />
      </PageContainer>
    );
  }

  const filled = cleanBlocks(draft.body);
  const canPublish = draft.title.trim().length > 0 && filled.length > 0;

  return (
    <PageContainer
      title="Website pages"
      subtitle="Delivery, returns and the other things a first-time customer checks before buying."
    >
      <div className="rounded-xl bg-gradient-to-r from-brand-600 to-indigo-600 p-6 text-white shadow-lg">
        <h2 className="text-lg font-bold">Tell customers what to expect</h2>
        <p className="mt-1 text-sm text-white/90">
          Most people who leave a shop they have never bought from leave because
          nobody answered “do you deliver to me?” or “what if I need to bring it
          back?”.
        </p>
      </div>

      <div className="mt-6 flex flex-wrap gap-2">
        {SITE_PAGE_KEYS.map((key) => {
          const page = pages?.find((p) => p.page_key === key);
          return (
            <button
              key={key}
              type="button"
              onClick={() => setActive(key)}
              className={`rounded-lg px-3 py-2 text-sm font-medium transition-all duration-200 ${
                key === active
                  ? 'bg-brand-600 text-white shadow-md'
                  : 'bg-neutral-100 text-neutral-700 hover:bg-brand-50 dark:bg-neutral-800 dark:text-neutral-200'
              }`}
            >
              {SITE_PAGE_LABELS[key]}
              {page && (
                <span className="ml-2">
                  <Badge variant={page.status === 'PUBLISHED' ? 'success' : 'neutral'}>
                    {page.status === 'PUBLISHED' ? 'Live' : 'Draft'}
                  </Badge>
                </span>
              )}
            </button>
          );
        })}
      </div>

      <div className="mt-6 rounded-xl border border-neutral-200 p-6 shadow-md dark:border-neutral-700">
        <p className="text-sm text-neutral-600 dark:text-neutral-400">
          {SITE_PAGE_HINTS[active]}
        </p>

        {current?.admin_noindex && (
          <Alert
            variant="warning"
            className="mt-4"
            description="We have kept this page out of search results. It is still visible to anyone who opens it. Contact support if you think this is wrong."
          />
        )}

        <div className="mt-4 space-y-4">
          <Input
            label="Page title"
            value={draft.title}
            onChange={(title) => setDraft((d) => ({ ...d, title }))}
            helperText="Shown as the heading and as the link in your website footer. Up to 80 characters."
          />

          <div>
            <p className="mb-2 text-sm font-medium text-neutral-800 dark:text-neutral-200">
              Content
            </p>
            <SitePageBlockEditor
              blocks={draft.body}
              max={MAX_BLOCKS}
              onChange={(body) => setDraft((d) => ({ ...d, body }))}
            />
          </div>

          <details className="rounded-lg border border-neutral-200 p-3 dark:border-neutral-700">
            <summary className="cursor-pointer text-sm font-medium">
              Search engine settings
            </summary>
            <div className="mt-3 space-y-3">
              <Input
                label="Search title"
                value={draft.meta_title}
                onChange={(meta_title) => setDraft((d) => ({ ...d, meta_title }))}
                helperText="Optional, up to 70 characters. Leave blank to use the page title."
              />
              <Input
                label="Search description"
                value={draft.meta_description}
                onChange={(meta_description) => setDraft((d) => ({ ...d, meta_description }))}
                helperText="Optional, up to 320 characters. Leave blank and your first paragraph is used."
              />
              <Toggle
                checked={draft.noindex}
                onChange={(noindex) => setDraft((d) => ({ ...d, noindex }))}
                label="Keep this page out of search results"
              />
              <p className="text-xs text-neutral-500 dark:text-neutral-400">
                It stays visible to anyone who opens it, and it stays in your
                website footer — this only asks search engines not to list it.
              </p>
            </div>
          </details>
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-3">
          <Button
            variant="primary"
            disabled={!canPublish || save.isPending}
            onClick={() => save.mutate({ status: 'PUBLISHED' })}
          >
            {current?.status === 'PUBLISHED' ? 'Update live page' : 'Publish'}
          </Button>
          <Button
            variant="secondary"
            disabled={save.isPending || !draft.title.trim()}
            onClick={() => save.mutate({ status: 'DRAFT' })}
          >
            Save as draft
          </Button>
          {current && (
            <Button variant="danger" disabled={remove.isPending} onClick={() => remove.mutate()}>
              Delete page
            </Button>
          )}
          {!canPublish && (
            <span className="text-sm text-neutral-500 dark:text-neutral-400">
              Add a title and at least one block to publish.
            </span>
          )}
        </div>

        {active === 'returns' && (
          <Alert
            variant="info"
            className="mt-4"
            description="Publishing this page also tells Google your returns window, on every product. Nothing is claimed until you publish it."
          />
        )}
      </div>
    </PageContainer>
  );
}
